import { Formik } from 'formik'
import { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useMutation } from 'react-query'
import { useParams } from 'react-router-dom'
import { object, string } from 'yup'

import messages from '@/components/Feedback/Dialog/messages'
import { Text } from '@/components/Form'
import Label from '@/components/Label'
import Modal from '@/components/Modal'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import CodeEditor from '@/pages/SpaceAppBuilder/components/CodeEditor/CodeEditor'
import * as actions from '@/pages/SpaceAppBuilder/FeaturesPage/actions'
import { TriggerTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { featureQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppDispatch } from '@/store/hooks'
import cn from '@/utilities/cn'
import { gzip } from '@/utilities/compression'

interface CreateRunnableFeatureModalProps {
  isOpen?: boolean
  onClose: () => void
  agentsFlagOn?: boolean
}

const CreateRunnableFeatureModal = ({
  isOpen = true,
  onClose,
  agentsFlagOn = false
}: CreateRunnableFeatureModalProps) => {
  const { spaceId, appId, featureId } = useParams<any>()
  const dispatch = useAppDispatch()
  const featureMutation = useMutation({
    mutationKey: featureQueryOptions({ spaceId, appId }).queryKey,

    onSuccess: async () => {
      // Temporary until we cut over to use react-query entirely
      await dispatch(actions.fetchAppInstanceFeature({ instanceId: appId, featureId, showLoader: false }))
      await dispatch(actions.fetchAppInstance(appId))
    }
  })
  const textboxRef = useRef(null)
  const isLoading = featureMutation.isLoading
  const [shouldValidateOnChange, setShouldValidateOnChange] = useState(false)

  const submitRunnableFeature = async (values: any) => {
    setShouldValidateOnChange(false)
    const trigger = {
      type: TriggerTypes.featureTrigger,
      name: `${agentsFlagOn ? 'Capability' : 'Feature'} Trigger`,
      triggerData: values.triggerData
    }

    // @ts-expect-error
    await featureMutation.mutateAsync([
      featureQueryOptions({ spaceId, appId }).queryKey[0],
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-workgrid-accept-encoding': 'workgrid-gzip',
          'x-workgrid-content-encoding': 'workgrid-gzip'
        },
        body: JSON.stringify(
          gzip({
            name: values.name,
            trigger: `${agentsFlagOn ? 'Capability' : 'Feature'} Trigger`,
            workflow: {
              trigger,
              steps: []
            }
          })
        )
      }
    ])
    onClose()
  }
  const validate = () => setShouldValidateOnChange(true)

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={shouldValidateOnChange}
      enableReinitialize
      validationSchema={object().shape({
        name: string()
          .min(2, `${agentsFlagOn ? 'Capability' : 'Feature'} name must be at least 2 characters`)
          .required(`${agentsFlagOn ? 'Capability' : 'Feature'} name is required`),
        payload: object().typeError(({ originalValue }) => {
          try {
            JSON.parse(originalValue)
          } catch {
            return 'Please enter valid JSON'
          }
          return true
        })
      })}
      onSubmit={submitRunnableFeature}
      initialValues={{
        name: '',
        triggerData: { payload: {} }
      }}
      validate={validate}
    >
      {({ handleSubmit, values, setFieldValue, errors, setFieldError }) => (
        <Modal isOpen={isOpen} initialFocus={textboxRef}>
          <form onSubmit={handleSubmit}>
            {isLoading && (
              <div className="card__overlay flex-center flex flex-col justify-center">
                <div role="status" className="absolute top-2/4 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <LoadingSpinner className="mr-2 h-12 w-12" />
                  <p className="mt-2 text-center text-base">Saving...</p>
                </div>
              </div>
            )}
            <div className="bg-white p-6 pb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Create {agentsFlagOn ? 'Capability' : 'Feature'}
              </h3>
              <p className="text-sm text-gray-500">
                Create a new {agentsFlagOn ? 'capability' : 'feature'} which can be run from another{' '}
                {agentsFlagOn ? 'capability' : 'feature'}
              </p>
              <div className="mt-6">
                <Label required>{agentsFlagOn ? 'Capability' : 'Feature'} Name</Label>
                <Text
                  className="mt-2"
                  name="name"
                  inputref={textboxRef}
                  placeholder={`Enter ${agentsFlagOn ? 'Capability' : 'Feature'} Name`}
                />
                <CodeEditor
                  label="Payload"
                  description={`Sample payload that will be passed to the ${agentsFlagOn ? 'capability' : 'feature'}`}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  defaultValue={
                    typeof values?.triggerData?.payload === 'string'
                      ? values.triggerData.payload
                      : JSON.stringify(values.triggerData.payload)
                  }
                  errors={errors}
                />
              </div>
            </div>
            <div className="ml-auto flex items-center justify-end bg-gray-50 py-3 px-6">
              <div className="flex flex-row-reverse gap-3">
                <button
                  type="submit"
                  onClick={() => handleSubmit()}
                  className={cn(
                    'inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700',
                    { 'pointer-events-none opacity-60': isLoading }
                  )}
                  disabled={isLoading}
                >
                  <FormattedMessage {...messages.submit} />
                </button>
                <button
                  type="button"
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={onClose}
                >
                  <FormattedMessage {...messages.cancel} />
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </Formik>
  )
}

export default CreateRunnableFeatureModal
